'use client';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { ComponentPropsWithoutRef, CSSProperties, Fragment } from 'react';
import { getPriceValueTranslation } from '@/entities/cart';
import { cn } from '@/shared/lib/cn';
import { Currency } from '@/shared/model/Currency';
import { PriceValue } from '@/shared/model/PriceValue';
import { As } from '@/shared/ui/as';
import { Form } from '@/shared/ui/form';
import { getMemberBoostPercent } from './addon-step.utils';
import { ProductDetailAddonsItemDescription } from './product-detail-addons-item-description';
import { ProductDetailAddonsItemName } from './product-detail-addons-item-name';
import './product-detail.scss';

type Props = {
    version?: 'grid' | 'row';
    max?: number;
    maxSelection?: number;
    isEdited?: boolean;
    title: string;
    description?: string;
    priceValue?: PriceValue;
    imageUrl?: string;
    showImage?: boolean;
    disableMinus?: boolean;
    disableUncheck?: boolean;
    contained?: number;
    amount?: number;
    packagePrice?: PriceValue;
} & ({ type?: 'input'; name: string; value?: never } | { type: 'radio'; value: string; name?: never }) &
    ComponentPropsWithoutRef<'div'>;

export const ProductDetailAddonsAmountItem = ({
    version = 'row',
    isEdited,
    max,
    maxSelection,
    title,
    name,
    type = 'input',
    value,
    imageUrl,
    showImage = true,
    description,
    priceValue,
    disableMinus,
    disableUncheck,
    contained = 0,
    amount = 0,
    packagePrice,
    ...props
}: Props) => {
    const t = useTranslations();
    const itemAmount = Number(priceValue?.amount ?? 0);
    const pkgAmount = Number(packagePrice?.amount ?? 0);
    const minus = contained > 0 && amount < contained;
    const selected = !minus && (contained > 0 ? amount >= contained : amount > 0);
    const extra = Math.max(0, amount - contained);
    const sectionMax = maxSelection ?? max;
    const boost = type === 'input' && extra >= 1 && (sectionMax ?? 2) > 1;
    const inPrice = contained > 0 && amount <= contained;
    const shownAmount = inPrice ? 0 : itemAmount + pkgAmount;
    const shownCurrency = (priceValue?.currency ?? packagePrice?.currency) as Currency;
    return (
        <div
            className={cn(
                'product-detail__addons-amount-item',
                version === 'row' && 'product-detail__addons-amount-item--row',
                version === 'row' && isEdited && !minus && 'product-detail__addons-amount-item--row-edited',
                version === 'grid' && 'product-detail__addons-amount-item--grid',
                version === 'grid' && isEdited && !minus && 'product-detail__addons-amount-item--grid-edited',
                selected && 'product-detail__addons-amount-item--selected',
                minus && 'product-detail__addons-amount-item--minus',
                boost && 'product-detail__addons-amount-item--boost',
            )}
            style={
                {
                    '--addon-boost': boost
                        ? `${getMemberBoostPercent(extra, sectionMax ? sectionMax - contained : null)}%`
                        : '0%',
                } as CSSProperties
            }
            {...props}
            data-testid="product-detail-addon-row"
            data-selected={selected ? 'true' : 'false'}
            data-boost={boost ? 'true' : 'false'}
        >
            <As
                as={version === 'row' ? 'div' : Fragment}
                className="product-detail__addons-amount-item-content"
            >
                {showImage &&
                    (imageUrl ? (
                        <Image
                            src={imageUrl}
                            alt="Produkt"
                            width={150}
                            height={150}
                            className={cn(
                                'product-detail__addons-amount-item-image',
                                version === 'row' && 'product-detail__addons-amount-item-image--row',
                                version === 'grid' && 'product-detail__addons-amount-item-image--grid',
                            )}
                            data-testid="product-detail-addon-image"
                        />
                    ) : (
                        <div
                            className={cn(
                                'product-detail__addons-amount-item-image',
                                version === 'row' && 'product-detail__addons-amount-item-image--row',
                                version === 'grid' && 'product-detail__addons-amount-item-image--grid',
                            )}
                            data-testid="product-detail-addon-image"
                        />
                    ))}
                <As
                    as={version === 'row' ? 'div' : Fragment}
                    className="product-detail__addons-amount-item-info"
                >
                    <ProductDetailAddonsItemName
                        className={cn(version === 'grid' && 'product-detail__addons-amount-item-name--grid')}
                        data-testid="product-detail-addon-name"
                    >
                        {title}
                        {contained > 0 && (
                            <span
                                className="product-detail__contained-badge"
                                data-testid="product-detail-addon-contained"
                            >
                                {t('PRODUCT_DETAIL.CONTAINED_COUNT', { count: contained })}
                            </span>
                        )}
                        {packagePrice && (
                            <span
                                className="product-detail__package-badge"
                                data-testid="product-detail-addon-package"
                            >
                                {t('PRODUCT_DETAIL.WITH_PACKAGE', {
                                    price: getPriceValueTranslation({
                                        amount: packagePrice.amount,
                                        currency: packagePrice.currency as Currency,
                                        t,
                                    }),
                                })}
                            </span>
                        )}
                    </ProductDetailAddonsItemName>
                    {version === 'row' && (
                        <ProductDetailAddonsItemDescription data-testid="product-detail-addon-description">
                            {description}
                        </ProductDetailAddonsItemDescription>
                    )}
                </As>
            </As>
            <div
                className={cn(
                    'product-detail__addons-amount-item-actions',
                    version === 'row' && 'product-detail__addons-amount-item-actions--row',
                    version === 'grid' && 'product-detail__addons-amount-item-actions--grid',
                )}
            >
                {(priceValue || packagePrice) && (
                    <span
                        className={cn(
                            'product-detail__addons-amount-item-price',
                            (inPrice || shownAmount === 0) &&
                                'product-detail__addons-amount-item-price--included',
                        )}
                        data-testid="product-detail-addon-price"
                        data-amount={inPrice || shownAmount === 0 ? '0' : String(shownAmount)}
                        data-currency={shownCurrency}
                    >
                        {inPrice || shownAmount === 0
                            ? t('PRODUCT_DETAIL.IN_PRICE')
                            : `+ ${getPriceValueTranslation({
                                  amount: shownAmount,
                                  currency: shownCurrency,
                                  t,
                              })}`}
                    </span>
                )}
                <div
                    className="product-detail__addons-amount-item-input-wrapper"
                    data-testid="product-detail__addons-amount-item-input-wrapper"
                >
                    {type === 'input' && name ? (
                        !(maxSelection ?? max) || (maxSelection ?? max ?? 0) > 1 ? (
                            <Form.AmountInput
                                name={name}
                                defaultValue={0}
                                className="w-full"
                                variant="hiding"
                                max={max}
                                disableMinus={disableMinus}
                                data-testid="product-detail-addon-amount-input"
                                minusButtonProps={{ 'data-testid': 'product-detail-addon-decrease' }}
                                plusButtonProps={{ 'data-testid': 'product-detail-addon-increase' }}
                            />
                        ) : (
                            <Form.AmountCheckbox
                                name={name}
                                // Without an explicit default the Controller still registers the
                                // field, leaving it `undefined` — which becomes
                                // `Number(undefined) === NaN` downstream and ships untouched
                                // members as order lines with quantity "NaN".
                                defaultValue={false}
                                disableUncheck={disableUncheck}
                                data-testid="product-detail-addon-checkbox"
                            />
                        )
                    ) : (
                        type === 'radio' && (
                            <Form.AmountRadio
                                value={value as string}
                                data-testid="product-detail-addon-radio"
                            />
                        )
                    )}
                </div>
            </div>
        </div>
    );
};
